import { DEFAULT_CLASS, type ClassId, DIFFICULTIES, DIFFICULTY_NAMES, MODE_NAMES, MODES, type Difficulty, type Mode } from "@eldrun/content";
import { NAME_MAX } from "@eldrun/protocol";
import { logo } from "@eldrun/web-ui/ui";
import { button, icon, panel } from "../kit/components.ts";
import { h, mount } from "../kit/dom.ts";
import type { KitIcon } from "../kit/kit-model.ts";
import type { Account } from "../../net/account.ts";
import { accountBox } from "./account-box.ts";
import { GAME_VERSION, latestNews, NEWS, newsDate } from "./news.ts";
import { readPref, writePref } from "../kit/storage.ts";
import { setTutorialEnabled } from "../hud/tutorial-model.ts";
import { loadTutorial, saveTutorial } from "../hud/tutorial-store.ts";
import { sharedSettingsPanel } from "../panels/settings-panel.ts";
import { rememberedClass } from "../theme/class-theme.ts";

/** Opções no menu (a mesma tela de jogo). */
function openSettings(onClose: () => void): void {
  const panel = sharedSettingsPanel();
  panel.onClose = onClose;
  panel.show();
}

/** Lema do jogo (o mesmo do site e do `package.json`). */
const TAGLINE = "Sozinho ou com até 3 amigos. Nenhum plano confiável.";
/** Quantas novidades o menu mostra. */
const NEWS_SHOWN = 3;

export interface MenuChoice {
  /** `sozinho` (F54): sala privada da Irmandade só sua, que começa na hora. */
  action: "publica" | "privada" | "codigo" | "sozinho";
  name: string;
  mode: Mode;
  difficulty: Difficulty;
  code?: string;
  /**
   * Classe (F64): a última escolhida na tela 3D (F85), lembrada no navegador. No lobby a escolha é na
   * própria tela da sala; o "Jogar sozinho" abre a escolha antes de entrar.
   */
  classId: ClassId;
  /** Id da conta que o menu mostrava (vazio: convidado); a entrada confere com a sessão de agora. */
  account: string;
}

/** Conta no menu (F34): sem ela, o menu é só de convidado. */
export interface MenuAccount {
  account: Account;
  siteUrl: string;
}

/** Linha de campo do kit: o nome (`<label for>`) à esquerda e o controle à direita. */
const fieldRow = (id: string, label: string, control: HTMLElement): HTMLElement => h("div", { class: "k-campo" }, h("label", { for: id, class: "k-campo-nome" }, label), control);

/** Link do rodapé com o contorno fino do kit, numa aba nova (o jogo fica aberto). */
const footerLink = (href: string, label: string, iconName: KitIcon): HTMLElement => h("a", { href, target: "_blank", rel: "noopener", class: "k-botao k-botao--secundario" }, icon(iconName), h("span", { class: "k-rotulo" }, label));

/**
 * Menu inicial (F80, mockup ui-a): a placa do título com o lema, os botões com ícone e › (sozinho, pública,
 * privada, código e Opções), a conta no canto, o painel da partida (apelido, modo, dificuldade, código e
 * tutorial), as novidades e o rodapé com o site e a versão. A classe é escolhida na tela 3D (F85); o acento
 * é o da última escolhida (neutro antes da primeira).
 */
export function showMenu(container: HTMLElement, onChoice: (choice: MenuChoice) => void, message?: string, conta?: MenuAccount): void {
  const name = h("input", { id: "nome", maxlength: NAME_MAX, placeholder: "Seu apelido", value: readPref("nome", ""), class: "k-entrada", autocomplete: "nickname" });
  const mode = h("select", { id: "modo", class: "k-seletor" }, ...MODES.map((m) => h("option", { value: m, selected: m === readPref("modo", "mutirao") }, MODE_NAMES[m])));
  const difficulty = h(
    "select",
    { id: "dificuldade", class: "k-seletor" },
    ...DIFFICULTIES.map((d) => h("option", { value: d, selected: d === readPref("dificuldade", "arretado") }, DIFFICULTY_NAMES[d])),
  );
  const code = h("input", { id: "codigo", maxlength: 8, placeholder: "Código da sala", class: "k-entrada", autocomplete: "off" });
  // Tutorial (F28): ligado na primeira vez; religar recomeça do primeiro passo.
  const tutorial = h("input", {
    type: "checkbox",
    id: "tutorial-opcao",
    class: "k-alternador",
    checked: loadTutorial().enabled,
    onchange: (e: Event) => {
      const on = (e.currentTarget as HTMLInputElement).checked;
      saveTutorial(setTutorialEnabled(loadTutorial(), on));
    },
  });
  // Com conta, o nome na sala é o apelido dela (o servidor confere): o campo mostra e trava. Ao
  // voltar a convidado, o apelido de convidado guardado volta; sem mudança, o digitado fica.
  let shown = "";
  const syncAccount = () => {
    const s = conta?.account.session();
    if (s) {
      name.value = s.apelido;
      name.title = "Com conta, o nome na sala é o apelido da conta.";
    } else if (shown) {
      name.value = readPref("nome", "");
      name.removeAttribute("title");
    }
    name.disabled = !!s;
    shown = s?.id ?? "";
    tutorial.checked = loadTutorial().enabled;
  };

  const choose = (action: MenuChoice["action"]) => () => {
    const choice: MenuChoice = { action, name: name.value, mode: mode.value as Mode, difficulty: difficulty.value as Difficulty, classId: rememberedClass() ?? DEFAULT_CLASS, account: shown };
    if (action === "codigo") choice.code = code.value;
    if (!shown) writePref("nome", name.value);
    writePref("modo", mode.value);
    writePref("dificuldade", difficulty.value);
    onChoice(choice);
  };
  const settingsClosed = () => (tutorial.checked = loadTutorial().enabled);
  const menuButton = (id: string, label: string, iconName: KitIcon, onClick: () => void) => {
    const el = button({ label, icon: iconName, variant: "menu", onClick });
    el.id = id;
    return el;
  };
  const byCode = menuButton("btn-codigo", "Entrar com código", "icone-codigo", choose("codigo"));
  // Enter no código entra na sala.
  code.addEventListener("keydown", (e) => {
    if (e.key === "Enter") byCode.click();
  });

  const news = latestNews(NEWS, NEWS_SHOWN).map((n) =>
    h(
      "li",
      { class: "novidade" },
      h("span", { class: "novidade-icone", "aria-hidden": "true" }, icon(n.icon)),
      h("div", { class: "novidade-texto" }, h("h3", {}, n.title), h("time", { datetime: n.date }, newsDate(n.date)), h("p", {}, n.text)),
    ),
  );
  const siteUrl = conta?.siteUrl;

  mount(
    container,
    h(
      "main",
      { class: "menu-inicial", "aria-label": "Menu inicial" },
      // A logo do Eldrun no título (F113): a única imagem de interface do jogo, a mesma do site (o `alt` é o nome do h1).
      h("header", { class: "menu-titulo" }, h("h1", { class: "menu-logo" }, logo({ width: 360, sizes: "(min-width: 640px) 360px, 70vw", priority: true, alt: "Eldrun" })), h("div", { class: "menu-titulo-divisor", "aria-hidden": "true" }), h("p", { class: "tagline" }, TAGLINE)),
      conta ? h("div", { class: "k-painel menu-conta" }, accountBox(conta.account, conta.siteUrl, syncAccount)) : null,
      h(
        "nav",
        { class: "menu-botoes", "aria-label": "Jogar" },
        message ? h("p", { class: "menu-aviso", role: "alert" }, message) : null,
        menuButton("btn-sozinho", "Jogar sozinho", "icone-jogar", choose("sozinho")),
        menuButton("btn-publica", "Partida pública", "icone-multijogador", choose("publica")),
        menuButton("btn-privada", "Sala privada", "icone-inicio", choose("privada")),
        byCode,
        menuButton("btn-opcoes", "Opções", "icone-opcoes", () => openSettings(settingsClosed)),
      ),
      h(
        "div",
        { class: "menu-partida" },
        panel(
          { title: "Partida", icon: "icone-controle" },
          fieldRow("nome", "Apelido", name),
          fieldRow("modo", "Modo", mode),
          fieldRow("dificuldade", "Dificuldade", difficulty),
          fieldRow("codigo", "Código da sala", code),
          h("label", { class: "k-campo" }, h("span", { class: "k-campo-nome" }, "Mostrar o tutorial"), tutorial),
          h("p", { class: "hint" }, "Irmandade de 1 a 4 jogadores (\"Jogar sozinho\" é nela e começa na hora); Arena de 2 a 4."),
        ),
      ),
      h("div", { class: "menu-novidades" }, panel({ title: "Novidades", icon: "icone-novidades" }, h("ul", { class: "novidades" }, ...news))),
      h(
        "footer",
        { class: "menu-rodape" },
        siteUrl ? footerLink(siteUrl, "Site", "icone-site") : null,
        siteUrl ? footerLink(`${siteUrl}/rankings`, "Rankings", "icone-rankings") : null,
        siteUrl ? footerLink(`${siteUrl}/feedback?de=jogo`, "Feedback", "icone-feedback") : null,
        h("span", { class: "menu-versao" }, `v${GAME_VERSION}`),
      ),
    ),
  );
  syncAccount();
}
